export class SolarOptimizer {
  constructor(
    hourlyConsumption = Array.from({ length: 4 }, () => Array(24).fill(3)), // Default: 4 seasons x 24 hours, filled with 3 kWh
    hourlyProduction = Array.from({ length: 365 }, () => Array(12).fill(5)), // Default: 365 days x 12 hours, filled with 5 kWh
    dischargeLimit = 0.2, // Default discharge limit
    maxPanelSize = 20, // Default max panel size in kW
    daysOfAutonomy = 2, // Default days of autonomy
    electricityPrice = 0.12, // Default electricity price in USD/kWh
    batteryPricePerkWh = 500, // Default battery price per kWh in USD
    panelPricePerkW = 1000, // Default price per kW for panels
    buybackRate = 0.05, // Default buyback rate
    minBatterySize = 2, // Default minimum battery size in kWh
    maxBatterySize = 10, // Default maximum battery size in kWh
    budget = 20000 // Default budget limit in USD
  ) {
    this.hourlyConsumption = hourlyConsumption;
    this.hourlyProduction = hourlyProduction;
    this.dischargeLimit = dischargeLimit;
    this.maxPanelSize = maxPanelSize;
    this.daysOfAutonomy = daysOfAutonomy;
    this.electricityPrice = electricityPrice;
    this.batteryPricePerkWh = batteryPricePerkWh;
    this.panelPricePerkW = panelPricePerkW;
    this.buybackRate = buybackRate;
    this.minBatterySize = minBatterySize;
    this.maxBatterySize = maxBatterySize;
    this.budget = budget;
  }
  getSeasonIndex(monthIndex) {
    // Define the season mapping
    const seasonMapping = [0, 0, 2, 2, 2, 1, 1, 1, 3, 3, 3, 0, 0, 0, 0, 0]; // Winter, Spring, Summer, Autumn

    // Return the season index
    return seasonMapping[monthIndex];
  }
  // On-Grid Calculations with Budget
  findMaxOnGridSetup() {
    let optimalPanelSize = 0;
    let optimalSavings = 0;
    let optimalCoveredConsumption = 0;
    let optimalExcessEnergy = 0;
    console.log("FINDMAX    ", this.hourlyProduction);
    for (
      let panelSize = 0.5;
      panelSize <= this.maxPanelSize;
      panelSize += 0.5
    ) {
      const panelCost = panelSize * this.panelPricePerkW;

      // Check if the panel cost exceeds the budget
      if (panelCost > this.budget) break;

      const [coveredConsumption, excessEnergy, totalSavings] =
        this.calculateOnGridSystem(panelSize);

      if (totalSavings > optimalSavings) {
        optimalPanelSize = panelSize;

        optimalSavings = totalSavings;
        optimalCoveredConsumption = coveredConsumption;
        optimalExcessEnergy = excessEnergy;
      }
    }

    const upfrontCost = Math.round(optimalPanelSize * this.panelPricePerkW);

    // Ensure the calculated upfront cost is within budget
    if (upfrontCost > this.budget) {
      return {
        error: "No feasible setup found within the budget limit.",
      };
    }

    return new SolarSetupDetails(
      "Maximized feed-in system within budget",
      Math.round(
        this.hourlyProduction.flat().reduce((a, b) => a + b, 0) *
          optimalPanelSize
      ),
      Math.round(optimalSavings),

      optimalCoveredConsumption /
        (optimalCoveredConsumption + optimalExcessEnergy),
      optimalPanelSize,
      0,
      upfrontCost
    );
  }

  calculateOnGridSystem(panelSize) {
    let coveredConsumption = 0;
    let excessEnergy = 0;
    let totalSavings = 0;

    for (
      let productionIndex = 0;
      productionIndex < this.hourlyProduction.length;
      productionIndex += 12
    ) {
      // Assuming that this.hourlyProduction is a flat array of daily 12-hour segments.
      // Calculate the season based on the current day
      const seasonIndex = this.getSeasonIndex(
        Math.floor(productionIndex / 12 / 30)
      ); // Correctly map day to season (4 seasons)

      for (let hour = 0; hour < 12; hour++) {
        const consumption = this.hourlyConsumption[seasonIndex][hour];

        const production =
          this.hourlyProduction[productionIndex + hour] * panelSize; // Adjusted for 1D access

        if (production >= consumption) {
          const excess = production - consumption;
          if (productionIndex % 1000 == 0) {
            // console.log("excessEnergy");
            //console.log(excessEnergy);
          }
          excessEnergy += excess;
          totalSavings +=
            consumption * this.electricityPrice + excess * this.buybackRate;
          coveredConsumption += consumption;
        } else {
          coveredConsumption += production;

          totalSavings += production * this.electricityPrice;
        }
      }
    }

    return [coveredConsumption, excessEnergy, totalSavings];
  }

  calculateConsumptionAndExcessEnergy(panelSize) {
    let coveredConsumption = 0;
    let excessEnergy = 0;

    try {
      for (
        let productionIndex = 0;
        productionIndex < this.hourlyProduction.length;
        productionIndex += 12
      ) {
        // Assuming that this.hourlyProduction is a flat array of daily 12-hour segments.
        // Calculate the season based on the current day
        const seasonIndex = this.getSeasonIndex(
          Math.floor(productionIndex / 12 / 30)
        ); // Correctly map day to season (4 seasons)

        for (let hour = 0; hour < 12; hour++) {
          const consumption = this.hourlyConsumption[seasonIndex][hour];

          const production =
            this.hourlyProduction[productionIndex + hour] * panelSize; // Adjusted for 1D access

          if (production > consumption) {
            excessEnergy += production - consumption;
            coveredConsumption += consumption;
          } else {
            coveredConsumption += production;
          }
        }
      }
    } catch (err) {
      console.error("An error occurred during the calculation:", err);
      return [0, 0]; // Return zero values in case of error
    }

    return [coveredConsumption, excessEnergy];
  }

  calculateConsumptionAndSavingsBattery(panelSize, batterySize) {
    let batteryCharge = batterySize;
    let uncoveredConsumption = 0;
    let coveredConsumption = 0;
    let totalSavings = 0;

    const dischargeLimit = this.dischargeLimit;

    for (
      let productionIndex = 0;
      productionIndex < this.hourlyProduction.length;
      productionIndex += 12
    ) {
      // Assuming that this.hourlyProduction is a flat array of daily 12-hour segments.
      // Calculate the season based on the current day
      const seasonIndex = this.getSeasonIndex(
        Math.floor(productionIndex / 12 / 30)
      ); // Correctly map day to season (4 seasons)

      for (let hour = 0; hour < 12; hour++) {
        const consumption = this.hourlyConsumption[seasonIndex][hour];
        const production =
          this.hourlyProduction[productionIndex + hour] * panelSize; // Adjusted for 1D access

        if (production > consumption) {
          const excessEnergy = production - consumption;
          const availableBatterySpace = batterySize - batteryCharge;
          batteryCharge += Math.min(excessEnergy, availableBatterySpace);
          coveredConsumption += consumption;
        } else {
          const deficit = consumption - production;
          const availableBatteryEnergy =
            batteryCharge - batterySize * dischargeLimit;

          if (availableBatteryEnergy > deficit) {
            batteryCharge -= deficit;
            coveredConsumption += deficit;
          } else {
            coveredConsumption += availableBatteryEnergy;
            uncoveredConsumption += deficit - availableBatteryEnergy;
            batteryCharge = batterySize * dischargeLimit;
          }
        }
      }
    }

    totalSavings = coveredConsumption * this.electricityPrice;

    return [coveredConsumption, uncoveredConsumption, totalSavings];
  }

  suggestLowerCostOptionByPercentagePlugIn(setupDetails) {
    console.log("Initial setupDetails:", setupDetails);

    let newPanelSize = setupDetails.panelSize * 0.67;
    console.log(
      "Calculated newPanelSize before applying lower limit:",
      newPanelSize
    );

    newPanelSize = Math.max(0.8, newPanelSize);
    console.log("newPanelSize after applying lower limit:", newPanelSize);

    const panelCostReduction =
      (setupDetails.panelSize - newPanelSize) * this.panelPricePerkW;
    console.log("Panel cost reduction:", panelCostReduction);

    const newUpFrontCost = Math.round(
      setupDetails.upFrontCost - panelCostReduction
    );
    console.log("New upfront cost:", newUpFrontCost);

    if (newUpFrontCost > this.budget) {
      console.log("Budget exceeded. Returning default values.");
      return {
        title: "Budget exceeded",
        power: 0,
        savings: 0,
        progress: 0,
        panelSize: 0,
        batterySize: 0,
        upFrontCost: newUpFrontCost,
      };
    }

    const [newConsumption, newExcessEnergy] =
      this.calculateConsumptionAndExcessEnergy(newPanelSize);
    console.log("New consumption:", newConsumption);
    console.log("New excess energy:", newExcessEnergy);

    const newSavings = newConsumption * this.electricityPrice;
    console.log("New savings:", newSavings);

    const totalProduction = this.hourlyProduction
      .flat()
      .reduce((sum, val) => sum + val, 0);
    console.log("Total production factor:", totalProduction);

    const power = Math.round(totalProduction * newPanelSize);
    console.log("Calculated power:", power);

    return new SolarSetupDetails(
      "Lower Cost Option",
      power,
      Math.round(newSavings),
      newConsumption / (newConsumption + newExcessEnergy),
      newPanelSize,
      0,
      newUpFrontCost
    );
  }

  findZeroExcessSetup() {
    let optimalPanelSize = 0;
    let minimalExcessEnergy = Number.MAX_VALUE;
    let maxCoveredConsumption = 0;

    for (
      let panelSize = 1;
      panelSize <= Math.floor(this.maxPanelSize);
      panelSize++
    ) {
      const totalCost = panelSize * this.panelPricePerkW;

      if (totalCost > this.budget) break;

      const [coveredConsumption, excessEnergy] =
        this.calculateConsumptionAndExcessEnergy(panelSize);

      if (
        excessEnergy < minimalExcessEnergy ||
        (excessEnergy === minimalExcessEnergy &&
          coveredConsumption > maxCoveredConsumption)
      ) {
        minimalExcessEnergy = excessEnergy;
        maxCoveredConsumption = coveredConsumption;
        optimalPanelSize = panelSize;
      }
    }

    return new SolarSetupDetails(
      "Minimized grid feed-in option",
      Math.round(
        this.hourlyProduction.flat().reduce((sum, val) => sum + val, 0)
      ),
      Math.round(maxCoveredConsumption * this.electricityPrice),

      maxCoveredConsumption / (maxCoveredConsumption + minimalExcessEnergy),
      optimalPanelSize,
      0,
      Math.round(optimalPanelSize * this.panelPricePerkW)
    );
  }

  findCoverConsumptionStandardBattery() {
    const stepSize = 0.5;
    let batterySize = this.minBatterySize;
    let optimalBatterySize = 0;
    let maxCoveredConsumption = 0;
    let maxUncoveredConsumption = 0;
    let optimalSavings = 0;

    while (batterySize <= this.maxBatterySize) {
      const totalCost =
        this.panelPricePerkW * this.maxPanelSize +
        this.batteryPricePerkWh * batterySize;

      if (totalCost > this.budget) break;

      const [coveredConsumption, uncoveredConsumption, savings] =
        this.calculateConsumptionAndSavingsBattery(
          this.maxPanelSize,
          batterySize
        );

      if (coveredConsumption > maxCoveredConsumption) {
        maxCoveredConsumption = coveredConsumption;
        optimalBatterySize = batterySize;
        optimalSavings = savings;
        maxUncoveredConsumption = uncoveredConsumption;
      }

      batterySize += stepSize;
    }

    return new SolarSetupDetails(
      "Standard consumption coverage",
      Math.round(
        this.hourlyProduction.flat().reduce((a, b) => a + b, 0) *
          this.maxPanelSize
      ),
      Math.round(optimalSavings),
      maxCoveredConsumption / (maxCoveredConsumption + maxUncoveredConsumption),
      this.maxPanelSize,
      optimalBatterySize,
      Math.round(
        this.panelPricePerkW * this.maxPanelSize +
          this.batteryPricePerkWh * optimalBatterySize
      )
    );
  }
  findFutureProofingBattery(standardSetup) {
    const newBatterySize = standardSetup.batterySize * 1.2;
    const [coveredConsumption, unCoveredConsumption, totalSavings] =
      this.calculateConsumptionAndSavingsBattery(
        standardSetup.panelSize,
        newBatterySize
      );

    return new SolarSetupDetails(
      "Future proofing (e.g. for buying an electric vehicle)",
      standardSetup.power,
      Math.round(totalSavings),
      coveredConsumption / (coveredConsumption + unCoveredConsumption),
      standardSetup.panelSize,
      newBatterySize,
      Math.round(
        this.panelPricePerkW * standardSetup.panelSize +
          this.batteryPricePerkWh * newBatterySize
      )
    );
  }
}

export class SolarSetupDetails {
  constructor(
    title,
    power,
    savings,
    progress,
    panelSize,
    batterySize,
    upFrontCost
  ) {
    this.title = title;
    this.power = power;
    this.savings = savings;
    this.progress = progress;
    this.panelSize = panelSize;
    this.batterySize = batterySize;
    this.upFrontCost = upFrontCost;
  }
}

// Export SolarOptimizer if needed for use in other modules
// module.exports = SolarOptimizer;
export function doRecommendations(
  chosenType,
  hourlyConsumption,
  hourlyProduction,
  budget,
  maxPanelSize
) {
  const feedBackRate = 0.035;
  const panelPrice = 700;
  const batteryPrice = 200;
  const solarOptimizer = new SolarOptimizer(
    hourlyConsumption,
    hourlyProduction,
    0.2, // optional
    maxPanelSize,
    1, // optional
    0.08, // optional
    batteryPrice, // optional
    panelPrice, // optional
    feedBackRate, // optional
    2, // optional
    10, // optional
    budget
  );

  switch (chosenType) {
    case 0:
      return OnGridRecommendations(solarOptimizer);
      break;
    case 1:
      return OffGridRecommendations(solarOptimizer);
      break;
    case 2:
      return PlugInRecommendations(solarOptimizer);
      break;
    default:
      return OnGridRecommendations(solarOptimizer);
  }
}
function OnGridRecommendations(solarOptimizer) {
  const maxOnGrid = solarOptimizer.findMaxOnGridSetup();
  const zeroExcess = solarOptimizer.findZeroExcessSetup();
  const plugInOption =
    solarOptimizer.suggestLowerCostOptionByPercentagePlugIn(maxOnGrid);
  return [maxOnGrid, zeroExcess, plugInOption];
}
function OffGridRecommendations(solarOptimizer) {
  const standardBattery = solarOptimizer.findCoverConsumptionStandardBattery();
  const future = solarOptimizer.findFutureProofingBattery(standardBattery);
  const plugInOption =
    solarOptimizer.suggestLowerCostOptionByPercentagePlugIn(standardBattery);
  return [standardBattery, future, plugInOption];
}
function PlugInRecommendations(solarOptimizer) {
  console.log("PLUGIN");
  const maxOnGrid = solarOptimizer.findMaxOnGridSetup();
  const zeroExcess = solarOptimizer.findZeroExcessSetup();
  const plugInOption =
    solarOptimizer.suggestLowerCostOptionByPercentagePlugIn(maxOnGrid);
  return [zeroExcess, plugInOption];
}
